/**
 * Stale build recovery (RMI-2026-713).
 *
 * After a deploy, a page that was opened BEFORE it still asks for the old hashed files (everything beyond the login screen is React.lazy, so those
 * files are only requested after sign-in). They no longer exist, the dynamic import fails, and the app sits on its loader forever -- seen on the
 * bedroom wall screen. Vite fires 'vite:preloadError' when that happens. The cure is to reload once so the page loads the current build.
 *
 * The cooldown is kept in sessionStorage so a reload that does not help (the server is down, the new build is also broken) is never repeated in a loop.
 */
export const STALE_RELOAD_KEY = 'tecc_stale_chunk_reload_at';
export const STALE_COOLDOWN_MS = 60 * 1000;

const safe = (fn, fallback) => { try { return fn(); } catch { return fallback; } };

/** Reloads unless it already did so within the cooldown. -> true when a reload was requested. */
export const recoverFromStaleChunk = ({ storage, reload, now = Date.now, cooldownMs = STALE_COOLDOWN_MS }) => {
  const t = now();
  const last = Number(safe(() => storage?.getItem(STALE_RELOAD_KEY), 0)) || 0;
  if (last && t - last < cooldownMs) return false;
  safe(() => storage?.setItem(STALE_RELOAD_KEY, String(t)));
  reload();
  return true;
};

/** Wires the handler to Vite's preload error event. `target` is window (injectable for tests). */
export const installStaleChunkRecovery = (target, storage, reload) => {
  const handler = (event) => {
    const reloading = recoverFromStaleChunk({ storage, reload });
    // Swallow the error only when we are really reloading. Inside the cooldown it must still surface (error boundary), so a persistent
    // problem is visible instead of hiding behind an empty module.
    if (reloading && event && typeof event.preventDefault === 'function') event.preventDefault();
  };
  target.addEventListener('vite:preloadError', handler);
  return () => target.removeEventListener('vite:preloadError', handler);
};
